import { useWorkerJobPositionsService } from '@restworld/data-services';
import { useCallback } from 'react';
import { JobApplication } from '@restworld/utility-types';
import {
  closeWorkerApplicationDataLoading,
  openWorkerApplicationDataLoading,
  proposedJobApplicationsAtom,
  inEvaluationJobApplicationsAtom,
  activeJobApplicationsAtom,
  pastJobApplicationsAtom,
  setProposedJobApplicationsAtom,
  setInEvaluationJobApplicationsAtom,
  setActiveJobApplicationsAtom,
  setPastJobApplicationsAtom,
  proposedJobApplicationsLoadingAtom,
  inEvaluationJobApplicationsLoadingAtom,
  activeJobApplicationsLoadingAtom,
  pastJobApplicationsLoadingAtom,
  proposedJobApplicationsHasMoreAtom,
  inEvaluationJobApplicationsHasMoreAtom,
  activeJobApplicationsHasMoreAtom,
  pastJobApplicationsHasMoreAtom,
} from '../global-state';
import { useSetAtom, useAtomValue } from 'jotai';

export type JobApplicationCategory = 'proposed' | 'in_evaluation' | 'active' | 'past';

export type UseLoadWorkerApplicationsProps = {
  showLoader?: boolean;
  limit?: number;
  offset?: number;
};

// Category offsets for pagination
const categoryOffsets = {
  proposed: 0,
  in_evaluation: 0,
  active: 0,
  past: 0,
};

// Helper function to get API parameters for each category
const getCategoryParams = (category: JobApplicationCategory, offset: number, limit: number) => {
  const baseParams = { limit, offset };

  switch (category) {
    case 'proposed':
      return {
        ...baseParams,
        status_key: 'worker_jp_proposed',
      };
    case 'in_evaluation':
      return {
        ...baseParams,
        status_key: 'worker_jp_interested',
      };
    case 'active':
      return {
        ...baseParams,
        status_sentiment: 'positive' as const,
        exclude_status_keys: ['worker_jp_interested', 'worker_jp_hired'],
      };
    case 'past':
      return {
        ...baseParams,
        status_sentiment: 'negative' as const,
        exclude_status_keys: 'worker_jp_hired',
      };
    default:
      return baseParams;
  }
};

export function useLoadWorkerApplications({
  showLoader = true,
  limit = 10,
  offset = 0,
}: UseLoadWorkerApplicationsProps) {
  const workerJobPositionService = useWorkerJobPositionsService();
  const openLoader = useSetAtom(openWorkerApplicationDataLoading);
  const closeLoader = useSetAtom(closeWorkerApplicationDataLoading);

  // Category atoms
  const proposedApplications = useAtomValue(proposedJobApplicationsAtom);
  const inEvaluationApplications = useAtomValue(inEvaluationJobApplicationsAtom);
  const activeApplications = useAtomValue(activeJobApplicationsAtom);
  const pastApplications = useAtomValue(pastJobApplicationsAtom);

  // Category setters
  const setProposedApplications = useSetAtom(setProposedJobApplicationsAtom);
  const setInEvaluationApplications = useSetAtom(setInEvaluationJobApplicationsAtom);
  const setActiveApplications = useSetAtom(setActiveJobApplicationsAtom);
  const setPastApplications = useSetAtom(setPastJobApplicationsAtom);

  // Loading setters
  const setProposedLoading = useSetAtom(proposedJobApplicationsLoadingAtom);
  const setInEvaluationLoading = useSetAtom(inEvaluationJobApplicationsLoadingAtom);
  const setActiveLoading = useSetAtom(activeJobApplicationsLoadingAtom);
  const setPastLoading = useSetAtom(pastJobApplicationsLoadingAtom);

  // Has more setters
  const setProposedHasMore = useSetAtom(proposedJobApplicationsHasMoreAtom);
  const setInEvaluationHasMore = useSetAtom(inEvaluationJobApplicationsHasMoreAtom);
  const setActiveHasMore = useSetAtom(activeJobApplicationsHasMoreAtom);
  const setPastHasMore = useSetAtom(pastJobApplicationsHasMoreAtom);

  // Load applications for a specific category
  const fetchCategoryApplications = useCallback(
    async (category: JobApplicationCategory, customOffset = 0) => {
      // Set loading state
      switch (category) {
        case 'proposed':
          setProposedLoading(true);
          break;
        case 'in_evaluation':
          setInEvaluationLoading(true);
          break;
        case 'active':
          setActiveLoading(true);
          break;
        case 'past':
          setPastLoading(true);
          break;
      }

      try {
        const params = getCategoryParams(category, customOffset, limit);
        const response = await workerJobPositionService.indexJobApplications(params);
        const newApplications = response?.data?.data;

        // Set applications and hasMore flag
        switch (category) {
          case 'proposed':
            setProposedApplications(newApplications);
            setProposedHasMore(newApplications?.length === limit);
            break;
          case 'in_evaluation':
            setInEvaluationApplications(newApplications);
            setInEvaluationHasMore(newApplications?.length === limit);
            break;
          case 'active':
            setActiveApplications(newApplications);
            setActiveHasMore(newApplications?.length === limit);
            break;
          case 'past':
            setPastApplications(newApplications);
            setPastHasMore(newApplications?.length === limit);
            break;
        }

        categoryOffsets[category] = customOffset;
      } catch (error) {
        console.error('Error fetching job applications:', error);
      } finally {
        // Clear loading state
        switch (category) {
          case 'proposed':
            setProposedLoading(false);
            break;
          case 'in_evaluation':
            setInEvaluationLoading(false);
            break;
          case 'active':
            setActiveLoading(false);
            break;
          case 'past':
            setPastLoading(false);
            break;
        }
      }
    },
    [
      workerJobPositionService,
      limit,
      setProposedLoading,
      setInEvaluationLoading,
      setActiveLoading,
      setPastLoading,
      setProposedApplications,
      setInEvaluationApplications,
      setActiveApplications,
      setPastApplications,
      setProposedHasMore,
      setInEvaluationHasMore,
      setActiveHasMore,
      setPastHasMore,
    ]
  );

  // Load more applications for a specific category
  const fetchMoreCategoryApplications = useCallback(
    async (category: JobApplicationCategory) => {
      // Get current applications
      let currentApplications: JobApplication[] = [];
      switch (category) {
        case 'proposed':
          currentApplications = proposedApplications;
          break;
        case 'in_evaluation':
          currentApplications = inEvaluationApplications;
          break;
        case 'active':
          currentApplications = activeApplications;
          break;
        case 'past':
          currentApplications = pastApplications;
          break;
      }

      const currentOffset = categoryOffsets[category];
      const nextOffset = currentOffset + limit;

      // Set loading state
      switch (category) {
        case 'proposed':
          setProposedLoading(true);
          break;
        case 'in_evaluation':
          setInEvaluationLoading(true);
          break;
        case 'active':
          setActiveLoading(true);
          break;
        case 'past':
          setPastLoading(true);
          break;
      }

      try {
        const params = getCategoryParams(category, nextOffset, limit);
        const response = await workerJobPositionService.indexJobApplications(params);
        const newApplications = response?.data?.data;

        const updatedApplications = [...currentApplications, ...newApplications];

        // Set updated applications and hasMore flag
        switch (category) {
          case 'proposed':
            setProposedApplications(updatedApplications);
            setProposedHasMore(newApplications?.length === limit);
            break;
          case 'in_evaluation':
            setInEvaluationApplications(updatedApplications);
            setInEvaluationHasMore(newApplications?.length === limit);
            break;
          case 'active':
            setActiveApplications(updatedApplications);
            setActiveHasMore(newApplications?.length === limit);
            break;
          case 'past':
            setPastApplications(updatedApplications);
            setPastHasMore(newApplications?.length === limit);
            break;
        }

        categoryOffsets[category] = nextOffset;
      } catch (error) {
        console.error('Error fetching more job applications:', error);
      } finally {
        // Clear loading state
        switch (category) {
          case 'proposed':
            setProposedLoading(false);
            break;
          case 'in_evaluation':
            setInEvaluationLoading(false);
            break;
          case 'active':
            setActiveLoading(false);
            break;
          case 'past':
            setPastLoading(false);
            break;
        }
      }
    },
    [
      workerJobPositionService,
      limit,
      proposedApplications,
      inEvaluationApplications,
      activeApplications,
      pastApplications,
      setProposedLoading,
      setInEvaluationLoading,
      setActiveLoading,
      setPastLoading,
      setProposedApplications,
      setInEvaluationApplications,
      setActiveApplications,
      setPastApplications,
      setProposedHasMore,
      setInEvaluationHasMore,
      setActiveHasMore,
      setPastHasMore,
    ]
  );

  // Load all categories with initial data
  const fetchWorkerApplications = useCallback(async () => {
    if (showLoader) openLoader();

    try {
      // Load all categories with initial data (10 items each)
      await Promise.all([
        fetchCategoryApplications('proposed', 0),
        fetchCategoryApplications('in_evaluation', 0),
        fetchCategoryApplications('active', 0),
        fetchCategoryApplications('past', 0),
      ]);

      if (showLoader) closeLoader();
    } catch (error) {
      if (showLoader) closeLoader();
      // Handle error silently or with proper error handling
    }
  }, [closeLoader, openLoader, showLoader, fetchCategoryApplications]);

  // Fetch next batch for a specific category
  const fetchNextBatch = useCallback(
    async (category: JobApplicationCategory) => {
      await fetchMoreCategoryApplications(category);
    },
    [fetchMoreCategoryApplications]
  );

  return {
    fetchWorkerApplications,
    fetchNextBatch,
    fetchCategoryApplications,
    fetchMoreCategoryApplications,
  };
}
